import React, { useState, useEffect } from 'react';
import { getAerodromes } from '../api/client';
import Cesium3DPreviewModal from './Cesium3DPreviewModal';

export default function KmlExportModal({ onClose, arps = null, projectId = null }) {
  const [aerodromes, setAerodromes] = useState([]);
  const [selectedIcao, setSelectedIcao] = useState('');
  const [mode, setMode] = useState('3d');
  const [loading, setLoading] = useState(true);
  const [show3DPreview, setShow3DPreview] = useState(false);

  useEffect(() => {
    if (arps) {
        setAerodromes(arps);
        if (arps.length > 0) setSelectedIcao(arps[0].icao);
        setLoading(false);
        return;
    }

    getAerodromes()
      .then(res => {
        // The API returns { aerodromes: [...] }
        const data = res.data.aerodromes || [];
        setAerodromes(data.sort((a, b) => a.icao.localeCompare(b.icao)));
        if (data.length > 0) {
          setSelectedIcao(data[0].icao);
        }
      })
      .catch(err => console.error("Failed to load aerodromes:", err))
      .finally(() => setLoading(false));
  }, [arps]);

  const handleExport = async () => {
    if (!selectedIcao) return;
    setLoading(true);
    
    try {
      const API_BASE = import.meta.env.VITE_API_URL || '/api';
      const downloadUrl = `${API_BASE}/ols-surfaces/${selectedIcao}/kml?mode=${mode}${projectId ? `&project_id=${projectId}` : ''}`;
      
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error("Network response was not ok");
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedIcao}_OLS_${mode.toUpperCase()}.kmz`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      onClose();
    } catch (err) {
      console.error("Export failed:", err);
      alert("Failed to export KMZ. Please check your network connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dg-modal-overlay">
      <div className="dg-modal" style={{ padding: 32, width: 420 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>
          Export OLS Surfaces
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 24 }}>
          Select an aerodrome to download its Obstacle Limitation Surfaces as a KMZ file for use in Google Earth or GIS software.
        </div>

        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
            Loading aerodromes...
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                Aerodrome
              </div>
              <select
                className="dg-input"
                value={selectedIcao}
                onChange={e => setSelectedIcao(e.target.value)}
              >
                {aerodromes.map(a => (
                  <option key={a.icao} value={a.icao}>
                    {a.icao} - {a.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                Export Mode
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { key: '3d', label: '3D — Google Earth', desc: 'KMZ with correct AMSL altitudes per section. Approach slopes and surface heights visible in 3D view.' },
                  { key: '2d', label: '2D — GIS / Flat',  desc: 'KMZ with flat footprints draped on terrain. Suitable for QGIS, ArcGIS, or 2D map overlay.' },
                ].map(opt => (
                  <div
                    key={opt.key}
                    onClick={() => setMode(opt.key)}
                    style={{
                      flex: 1, padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                      border: `1px solid ${mode === opt.key ? 'var(--blue-glass-border)' : 'var(--glass-border)'}`,
                      background: mode === opt.key ? 'var(--blue-glass-fill)' : 'var(--glass-fill)',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600, color: mode === opt.key ? 'var(--text-blue)' : 'var(--text-primary)', marginBottom: 4 }}>
                      {opt.label}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                      {opt.desc}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
          <button className="dg-btn dg-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="dg-btn dg-btn-ghost"
            onClick={() => setShow3DPreview(true)}
            disabled={loading || !selectedIcao}
            title="Preview these OLS surfaces and project obstacles in 3D, right here"
            style={{ opacity: loading || !selectedIcao ? 0.5 : 1, color: '#2ECC71', borderColor: '#2ECC7144' }}
          >
            🌐 Preview 3D
          </button>
          <button
            className="dg-btn dg-btn-primary"
            onClick={handleExport}
            disabled={loading || !selectedIcao}
            style={{ opacity: loading || !selectedIcao ? 0.5 : 1 }}
          >
            Download KMZ
          </button>
        </div>
      </div>

      {show3DPreview && (
        <Cesium3DPreviewModal
          title={`${selectedIcao} — OLS 3D Preview`}
          kmlUrl={`${import.meta.env.VITE_API_URL || '/api'}/ols-surfaces/${selectedIcao}/kml?mode=3d${projectId ? `&project_id=${projectId}` : ''}`}
          onClose={() => setShow3DPreview(false)}
        />
      )}
    </div>
  );
}
